import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import useIntl from '../../hooks/useIntl';
import messages from './messages';

// A floor so a non-empty stage still shows a legible bar. Zero-count stages
// render as a hollow track instead (see StageRow), so "empty" never looks like
// "one lead".
const MIN_BAR_PERCENT = 12;

// Shared 3-column grid: stage name | centered bar | share readout. The header
// row and every stage row use this so the columns line up.
const GRID_COLUMNS = '168px 1fr 56px';

const titleCase = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Quiet, theme-aware group accents assigned by group ORDER, not by name, so a
// renamed or added group simply takes the next hue and no stage vocabulary is
// hardcoded here. Kept soft — the loud/attention palette stays reserved.
//
// DEVIATION (reviewer sign-off requested): these are intentional literal
// hex/rgba values rather than theme palette tokens, which the repo styling
// guideline normally requires. The funnel needs three DISTINCT, quiet group
// hues with matched light/dark variants, and the shared theme palette does not
// expose three such neutral accents to derive from. Left as literals on
// purpose; a proper theme-token treatment is expected to arrive with the
// upcoming Lead Management redesign. See Copilot thread on this line.
const GROUP_ACCENTS = [
  {
    light: {
      main: '#0b7285',
      fill: 'rgba(11,114,133,0.12)',
      ring: 'rgba(11,114,133,0.30)',
    },
    dark: {
      main: '#5bd1e6',
      fill: 'rgba(91,209,230,0.14)',
      ring: 'rgba(91,209,230,0.34)',
    },
  },
  {
    light: {
      main: '#3b5b98',
      fill: 'rgba(59,91,152,0.12)',
      ring: 'rgba(59,91,152,0.30)',
    },
    dark: {
      main: '#8aa2ff',
      fill: 'rgba(138,162,255,0.16)',
      ring: 'rgba(138,162,255,0.34)',
    },
  },
  {
    light: {
      main: '#5c6270',
      fill: 'rgba(92,98,112,0.12)',
      ring: 'rgba(92,98,112,0.28)',
    },
    dark: {
      main: '#98a2b3',
      fill: 'rgba(152,162,179,0.14)',
      ring: 'rgba(152,162,179,0.30)',
    },
  },
];

const accentFor = (index) => GROUP_ACCENTS[index % GROUP_ACCENTS.length];

// One stage row: name in a fixed column, a centered bar whose width is honest
// against the largest stage, and the stage's share of the active pipeline on
// the right. The whole row is the click target.
const StageRow = ({
  stage,
  maxCount,
  totalActive,
  accent,
  selected,
  onSelect,
}) => {
  const isEmpty = stage.count === 0;
  const percent = isEmpty
    ? 0
    : Math.max(MIN_BAR_PERCENT, (stage.count / maxCount) * 100);
  const share =
    totalActive > 0 ? Math.round((stage.count / totalActive) * 100) : 0;

  // Bar fill by state: selected wins, then empty (hollow), then group tint.
  const barSurface = (theme) => {
    const tint = accent[theme.palette.mode] || accent.light;
    if (selected) {
      return {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      };
    }
    if (isEmpty) {
      return {
        backgroundColor: 'transparent',
        color: 'text.disabled',
        border: '1px dashed',
        borderColor: 'divider',
      };
    }
    return {
      backgroundColor: tint.fill,
      color: tint.main,
      boxShadow: `inset 0 0 0 1px ${tint.ring}`,
    };
  };

  const rowButton = (
    <Box
      component="button"
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(stage.key)}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: 'center',
        gap: 2,
        width: '100%',
        textAlign: 'left',
        p: 0.75,
        border: 'none',
        borderRadius: 1,
        background: 'transparent',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
        },
        ...(selected && { backgroundColor: 'action.selected' }),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ minWidth: 0 }}
      >
        <Typography
          variant="bodyMedium"
          title={stage.label}
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {stage.label}
        </Typography>
        {/* ENG-34127 — a visible info icon signals there's help to hover. The
            Tooltip itself wraps the whole row button (below) so it opens on hover
            AND keyboard focus; the icon is a purely visual cue. */}
        {stage.tooltip ? (
          <AlertInfoIcon
            fontSize={'xSmall'}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
        ) : null}
      </Stack>

      {/* Centered bar → the widths read as a funnel narrowing to the close. */}
      <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 30,
            minWidth: 46,
            px: 1.25,
            borderRadius: 1,
            transition: 'width 200ms ease',
            width: isEmpty ? 46 : `${percent}%`,
            ...barSurface(theme),
          })}
        >
          <Typography
            variant="labelMedium"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {stage.count}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="labelSmall"
        sx={{
          textAlign: 'right',
          color: isEmpty ? 'text.disabled' : 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {`${share}%`}
      </Typography>
    </Box>
  );

  // ENG-34127 — wrap the focusable row button (not the decorative icon) so the
  // tooltip opens on hover AND keyboard focus, and sets aria-describedby on the
  // element that already takes focus (describeChild). containerSX keeps the row
  // full width, avoiding the fit-content shrink a bare Tooltip wrapper causes.
  return stage.tooltip ? (
    <Tooltip
      title={stage.tooltip}
      placement={'top-start'}
      variant={'light'}
      arrow={true}
      describeChild
      containerSX={{ width: '100%' }}
    >
      {rowButton}
    </Tooltip>
  ) : (
    rowButton
  );
};

const Funnel = ({
  groupedStages,
  maxCount,
  totalActive,
  selectedStageKey,
  onSelectStage,
}) => {
  const intl = useIntl();

  return (
    <Stack spacing={2}>
      {/* Header hint for the right-hand share readout. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          gap: 2,
          px: 0.75,
        }}
      >
        <Box />
        <Box />
        <Typography
          variant="labelSmall"
          sx={{
            textAlign: 'right',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {intl.formatMessage(messages.shareColumn)}
        </Typography>
      </Box>

      {groupedStages.map((group, index) => {
        const accent = accentFor(index);
        return (
          <Box key={group.group}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box
                sx={(theme) => {
                  const tint = accent[theme.palette.mode] || accent.light;
                  return {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: tint.main,
                  };
                }}
              />
              <Typography
                variant="labelSmall"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {titleCase(group.group)}
              </Typography>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {group.stages.map((stage) => (
                <StageRow
                  key={stage.key}
                  stage={stage}
                  maxCount={maxCount}
                  totalActive={totalActive}
                  accent={accent}
                  selected={stage.key === selectedStageKey}
                  onSelect={onSelectStage}
                />
              ))}
            </Stack>
            {index < groupedStages.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        );
      })}
    </Stack>
  );
};

export default Funnel;
